import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { fileUrl } from "@/lib/api";

const tileAnim = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-30px" },
};

// Listes triées par ordre admin, éléments masqués exclus
export const visibleOrdered = (list) =>
  [...(list || [])].filter((x) => !x.hidden).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

function firstImage(products, match) {
  const p = products.find((x) => match(x) && x.images?.length);
  return p ? fileUrl(p.images[0]) : null;
}

// Règle d'affichage d'une marque dans une catégorie :
// - jamais si masquée ou non associée à cette catégorie dans l'admin
// - « coming soon » : affichée si associée (lien vers Coming soon)
// - disponible : affichée si associée ET au moins un produit dans la catégorie
export function brandVisibleIn(brand, catSlug, products) {
  if (brand.status === "hidden") return false;
  if (!(brand.categories || []).includes(catSlug)) return false;
  if (brand.status === "coming_soon") return true;
  return products.some((p) => p.brand === brand.name && p.category === catSlug);
}

function BigCard({ label, count, image, soon, to, active, testid, delay = 0 }) {
  return (
    <motion.div {...tileAnim} transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}>
      <Link
        to={to}
        data-testid={testid}
        className={`group relative overflow-hidden rounded-3xl border text-left h-36 sm:h-44 w-full block bg-gradient-to-br from-kay-rose to-kay-rosedeep/40 transition-all duration-300 ${
          active ? "border-kay-ink shadow-[0_20px_45px_rgba(18,18,18,0.25)]" : "border-kay-line shadow-[0_10px_30px_rgba(232,160,191,0.12)] hover:shadow-[0_20px_45px_rgba(224,82,151,0.25)]"
        }`}
      >
        {image && (
          <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-110" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-kay-ink/70 via-kay-ink/10 to-transparent" />
        {soon && (
          <span className="absolute top-3 right-3 bg-kay-rose text-kay-ink font-mono text-[10px] font-bold tracking-widest px-2.5 py-1 rounded-full" data-testid={`${testid}-soon-badge`}>
            BIENTÔT
          </span>
        )}
        <div className="absolute bottom-0 inset-x-0 p-4 flex items-end justify-between gap-2">
          <div>
            <p className="font-display text-xl sm:text-2xl text-white leading-tight group-hover:italic transition-all">{label}</p>
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/70 mt-1">
              {soon ? "Ça arrive…" : `${count} pépite${count > 1 ? "s" : ""}`}
            </p>
          </div>
          <span className="w-8 h-8 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity">
            <ArrowRight size={14} />
          </span>
        </div>
      </Link>
    </motion.div>
  );
}

function Bubble({ label, count, active, soon, onClick, testid, delay = 0 }) {
  return (
    <motion.button
      type="button"
      {...tileAnim}
      transition={{ duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] }}
      onClick={onClick}
      data-testid={testid}
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.96 }}
      className={`relative rounded-2xl px-5 py-4 text-left border transition-all duration-300 min-w-[130px] ${
        active
          ? "bg-kay-ink text-white border-kay-ink shadow-[0_14px_35px_rgba(18,18,18,0.25)]"
          : soon
            ? "bg-kay-rose/30 border-dashed border-kay-rosedeep/60 hover:bg-kay-rose/50"
            : "bg-white border-kay-line hover:border-kay-pink hover:shadow-[0_14px_30px_rgba(232,160,191,0.25)]"
      }`}
    >
      <p className={`font-display text-lg leading-tight ${active ? "italic" : ""}`}>{label}</p>
      <p className={`font-mono text-[10px] tracking-[0.2em] uppercase mt-1 ${active ? "text-kay-rose" : "text-kay-pink"}`} data-testid={soon ? `${testid}-soon-badge` : undefined}>
        {soon ? "Bientôt" : `${count} produit${count > 1 ? "s" : ""}`}
      </p>
      {soon && <Sparkles size={12} className="absolute top-3 right-3 text-kay-pink" />}
    </motion.button>
  );
}

// Grandes cartes « univers » — chaque carte est un vrai lien vers sa page dédiée
export function UniverseCards({ products, settings, activeSlug = null }) {
  const cats = visibleOrdered(settings?.categories);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="universe-cards">
      <BigCard
        label="Toutes les pépites"
        count={products.length}
        image={(settings?.all_image && fileUrl(settings.all_image)) || firstImage(products, () => true)}
        to="/boutique"
        active={activeSlug === null}
        testid="catnav-cat-tout"
        delay={0}
      />
      {cats.map((c, i) => {
        const inCat = products.filter((p) => p.category === c.slug);
        return (
          <BigCard
            key={c.slug}
            label={c.label}
            count={inCat.length}
            image={(c.image && fileUrl(c.image)) || firstImage(products, (p) => p.category === c.slug)}
            soon={inCat.length === 0}
            to={`/boutique/${c.slug}`}
            active={activeSlug === c.slug}
            testid={`catnav-cat-${c.slug}`}
            delay={0.07 * (i + 1)}
          />
        );
      })}
    </div>
  );
}

// Navigation d'une page catégorie : sous-catégories (bulles) + marques associées
export default function CategoryNav({ products, settings, category, sub, brand, onSub, onBrand }) {
  const subs = visibleOrdered(settings?.subcategories).filter((s) => s.category === category);
  const brands = visibleOrdered(settings?.brands);
  const catBrands = category ? brands.filter((b) => brandVisibleIn(b, category, products)) : brands.filter((b) => b.status !== "hidden" && (b.status === "coming_soon" ? (b.categories || []).length > 0 : products.some((p) => p.brand === b.name)));
  const scoped = sub ? products.filter((p) => p.subcategory === sub) : products;

  return (
    <div className="mt-10 grid gap-8" data-testid="category-nav">
      {subs.length > 0 && (
        <div>
          <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-kay-pink mb-4">Par envie</p>
          <div className="flex flex-wrap gap-3">
            <Bubble label="Toutes" count={products.length} active={!sub} onClick={() => onSub(null)} testid="catnav-sub-toutes" />
            {subs.map((s, i) => {
              const n = products.filter((p) => p.subcategory === s.slug).length;
              return (
                <Bubble
                  key={s.slug}
                  label={s.label}
                  count={n}
                  soon={n === 0}
                  active={sub === s.slug}
                  onClick={() => onSub(s.slug)}
                  testid={`catnav-sub-${s.slug}`}
                  delay={0.05 * (i + 1)}
                />
              );
            })}
          </div>
        </div>
      )}

      {catBrands.length > 0 && (
        <div>
          <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-kay-pink mb-4">Par marque</p>
          <div className="flex flex-wrap gap-3">
            <Bubble label="Toutes" count={scoped.length} active={brand === "all"} onClick={() => onBrand("all")} testid="catnav-brand-all" />
            {catBrands.map((b, i) => {
              const soon = b.status === "coming_soon";
              const n = scoped.filter((p) => p.brand === b.name).length;
              if (soon) {
                return (
                  <motion.div key={b.name} {...tileAnim} transition={{ duration: 0.45, delay: 0.05 * (i + 1), ease: [0.22, 1, 0.36, 1] }}>
                    <Link
                      to="/coming-soon"
                      data-testid={`catnav-brand-soon-${b.name.toLowerCase().replace(/\s/g, "-")}`}
                      className="block rounded-2xl px-5 py-4 border border-dashed border-kay-rosedeep bg-gradient-to-br from-kay-rose/60 to-kay-rosedeep/30 hover:shadow-[0_14px_30px_rgba(232,160,191,0.3)] hover:-translate-y-1 transition-all duration-300 min-w-[130px]"
                    >
                      <p className="font-display text-lg leading-tight">{b.name}</p>
                      <p className="font-mono text-[10px] tracking-[0.2em] uppercase mt-1 text-kay-pink flex items-center gap-1">
                        <Sparkles size={11} /> Coming soon
                      </p>
                    </Link>
                  </motion.div>
                );
              }
              return (
                <Bubble
                  key={b.name}
                  label={b.name}
                  count={n}
                  active={brand === b.name}
                  onClick={() => onBrand(b.name)}
                  testid={`catnav-brand-${b.name.toLowerCase().replace(/\s/g, "-")}`}
                  delay={0.05 * (i + 1)}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
